import { Lock } from 'lucide-react';
import {
  CONFIGURABLE_CAPABILITY_ROLES,
  ROLE_CAPABILITIES,
  SYSTEM_ROLE_LABELS,
  SystemRole,
} from '@peles/shared';

type Definition = (typeof ROLE_CAPABILITIES)[number];
import { useDialog } from '../../context/DialogContext';
import { useCapabilityGrid, useSetCapability } from '../../hooks/useCapabilities';

interface RoleCapabilitiesGridProps {
  /** Only the capabilities of this area (e.g. `Messaging`); every area when left out. */
  area?: string;
}

/**
 * Who may do what, by role — the office's grid in Settings.
 *
 * An administrator may do everything and is shown locked, so nobody can take
 * the office's own keys away. Clients and vendors are not here: what they may
 * do is granted per event, on the event. Each change is saved at once, and the
 * server checks every request against it (`authorizeCapability`).
 */
export function RoleCapabilitiesGrid({ area }: RoleCapabilitiesGridProps) {
  const { data: cells, isLoading, isError } = useCapabilityGrid();
  const setCapability = useSetCapability();
  const { alert } = useDialog();

  const definitions = ROLE_CAPABILITIES.filter(
    (definition) => area === undefined || definition.area === area,
  );
  const allowed = (role: SystemRole, definition: Definition) =>
    cells?.find((cell) => cell.role === role && cell.capability === definition.key)?.allowed ??
    definition.defaultAllowed;

  const toggle = (role: SystemRole, definition: Definition, value: boolean) => {
    setCapability.mutate(
      { role, capability: definition.key, allowed: value },
      {
        onError: () =>
          void alert({
            title: 'Not saved',
            message: `“${definition.label}” could not be changed. Please try again.`,
            variant: 'error',
          }),
      },
    );
  };

  if (isError) {
    return <p className="text-sm text-red-600">Who may do what could not be loaded.</p>;
  }

  return (
    <section aria-labelledby="role-capabilities-heading" className="mb-8 max-w-3xl">
      <h3 id="role-capabilities-heading" className="text-base font-semibold text-gray-900">
        Who may do what
      </h3>
      <p className="text-sm text-gray-600 mt-1 mb-3">
        What each role may send. Clients and vendors are given messaging per event, on the event
        itself.
      </p>
      <div className="rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-gray-500">
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium text-center w-24">
                {SYSTEM_ROLE_LABELS[SystemRole.ADMIN]}
              </th>
              {CONFIGURABLE_CAPABILITY_ROLES.map((role) => (
                <th key={role} className="px-3 py-2 font-medium text-center w-24">
                  {SYSTEM_ROLE_LABELS[role]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {definitions.map((definition) => (
              <tr key={definition.key} className="border-b border-gray-100 last:border-0">
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{definition.label}</div>
                  <div className="text-xs text-gray-500">{definition.description}</div>
                </td>
                <td className="px-3 py-2 text-center">
                  <span
                    className="inline-flex items-center text-gray-400"
                    title="Administrators may always do this"
                  >
                    <Lock className="h-4 w-4" aria-label="Always allowed" />
                  </span>
                </td>
                {CONFIGURABLE_CAPABILITY_ROLES.map((role) => (
                  <td key={role} className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      aria-label={`${SYSTEM_ROLE_LABELS[role]}: ${definition.label}`}
                      checked={allowed(role, definition)}
                      disabled={isLoading || setCapability.isPending}
                      onChange={(event) => toggle(role, definition, event.target.checked)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
